/**
 * Official Fantasy Premier League constraints.
 *
 * Money is held in tenths of a million, matching the FPL API's `now_cost`
 * field (£12.5m is 125). Keeping the same unit end-to-end avoids float drift
 * when summing 15 prices against a hard budget.
 */

/** Gameweeks in a full FPL season — the denominator for "how much season is
 * left", not a squad or transfer rule, but every bit as official. */
export const TOTAL_GAMEWEEKS = 38;

export const POSITIONS = ['GKP', 'DEF', 'MID', 'FWD'] as const;
export type Position = (typeof POSITIONS)[number];

/** FPL's `element_type` ids, as returned by bootstrap-static. */
export const ELEMENT_TYPE_TO_POSITION: Record<number, Position> = {
  1: 'GKP',
  2: 'DEF',
  3: 'MID',
  4: 'FWD',
};

export const SQUAD_RULES = {
  size: 15,
  /** Exact number of each position a legal squad must contain. */
  byPosition: { GKP: 2, DEF: 5, MID: 5, FWD: 3 } as Record<Position, number>,
  maxPerClub: 3,
  /** £100.0m, in tenths. */
  budget: 1000,
} as const;

export const XI_RULES = {
  size: 11,
  min: { GKP: 1, DEF: 3, MID: 2, FWD: 1 } as Record<Position, number>,
  max: { GKP: 1, DEF: 5, MID: 5, FWD: 3 } as Record<Position, number>,
} as const;

export const TRANSFER_RULES = {
  freePerGameweek: 1,
  /** Unused free transfers bank up to this many. */
  maxBanked: 5,
  /** Points deducted per transfer beyond the free allowance. */
  pointsPerExtraTransfer: 4,
} as const;

export const CHIPS = ['wildcard', 'freehit', 'bboost', '3xc'] as const;
export type Chip = (typeof CHIPS)[number];

export const CHIP_LABELS: Record<Chip, string> = {
  wildcard: 'Wildcard',
  freehit: 'Free Hit',
  bboost: 'Bench Boost',
  '3xc': 'Triple Captain',
};

/** Wildcard has two uses per season (one per half); every other chip has one. */
export const CHIP_USES_PER_SEASON: Record<Chip, number> = {
  wildcard: 2,
  freehit: 1,
  bboost: 1,
  '3xc': 1,
};

/** Convert tenths-of-a-million to a display string: 125 -> "£12.5m", and
 * -8 -> "-£0.8m" (the sign goes before the £, not between it and the digits —
 * overspending the bank is the one place this app shows a negative price). */
export function formatPrice(tenths: number): string {
  const sign = tenths < 0 ? '-' : '';
  return `${sign}£${Math.abs(tenths / 10).toFixed(1)}m`;
}

export function parsePrice(display: string): number {
  const match = /£?\s*(\d+(?:\.\d+)?)\s*m?/i.exec(display.trim());
  if (!match?.[1]) throw new Error(`cannot parse price: ${display}`);
  return Math.round(Number(match[1]) * 10);
}
